import { SmartLinkFilter } from '../smlFilterTabs/types';
import { SMLRouteNames } from '../../../routing/sml/SMLRoutes';

/** The query keys of the SmartLinks page: the tab and the search text. */
export const SMARTLINKS_TAB_PARAM = 'tab';
export const SMARTLINKS_SEARCH_PARAM = 'search';

/** Cards that each request of the grid loads. The API takes at most 100. */
export const SMARTLINKS_PAGE_SIZE = 24;

/** The old full list (`/app/all`) is gone. Its path stays only for the redirect. */
export const LEGACY_ALL_SMARTLINKS_PATH = 'all';

/** The tab of a `?tab=` value. A missing or unknown value is `all`. */
export function smartLinksTabOf(value: string | null): SmartLinkFilter {
  return value === 'live' || value === 'draft' ? value : 'all';
}

/** The `status` of the list request for a tab. `all` sends none. */
export function smartLinksStatusOf(tab: SmartLinkFilter): 'live' | 'draft' | undefined {
  return tab === 'all' ? undefined : tab;
}

/**
 * The query of the SmartLinks page after a change of the tab or the search. It keeps the other
 * keys (`accountId`), drops `tab=all` and an empty search, so the plain page has no query.
 */
export function smartLinksOverviewQuery(
  current: URLSearchParams,
  change: { tab?: SmartLinkFilter; search?: string }
): URLSearchParams {
  const next = new URLSearchParams(current);
  if (change.tab !== undefined) {
    if (change.tab === 'all') {
      next.delete(SMARTLINKS_TAB_PARAM);
    } else {
      next.set(SMARTLINKS_TAB_PARAM, change.tab);
    }
  }
  if (change.search !== undefined) {
    const search = change.search.trim();
    if (search === '') {
      next.delete(SMARTLINKS_SEARCH_PARAM);
    } else {
      next.set(SMARTLINKS_SEARCH_PARAM, change.search);
    }
  }
  return next;
}

/**
 * Where an old `/all` link goes: the SmartLinks page. The search, a `tab` (or an old `status`)
 * and `accountId` stay; `page` is dropped, because the page loads more cards in place.
 */
export function legacyAllSmartLinksTarget(search: string): string {
  const old = new URLSearchParams(search);
  const next = new URLSearchParams();
  const accountId = old.get('accountId');
  if (accountId !== null && accountId !== '') {
    next.set('accountId', accountId);
  }
  const tab = smartLinksTabOf(old.get(SMARTLINKS_TAB_PARAM) ?? old.get('status'));
  const query = smartLinksOverviewQuery(next, { tab, search: old.get(SMARTLINKS_SEARCH_PARAM) ?? '' }).toString();
  return `/${SMLRouteNames.smartlinks}${query === '' ? '' : `?${query}`}`;
}
